<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    :totalSections="1"
  />
  <OperationFormLayout
    v-if="!loading && currentOrganisation"
    :id="processId"
    :formDisabled="!imageValid"
    :formSaving="saving"
    operationType="process"
    @cancel="$router.replace({ name: 'equipmentProcessConfiguration' })"
    @update="save"
  >
    <OperationFormSection
      :modifiers="['border-radius-top', 'border-radius-bottom']"
      :sectionDescription="$t('configuration.process.details_description')"
      :sectionTitle="$t('configuration.process.details_title')"
    >
      <FormRow
        :errors="errors?.equipment_process?.name"
        :label="$t('configuration.process.name_label')"
        labelFor="process_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': errors?.equipment_process?.name }">
            <input
              id="process_name"
              v-model="process.name"
              :placeholder="$t('configuration.process.input_name')"
              @input="validateInput('process', 'name')"
            >
          </div>
        </div>
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>
</template>

<script setup>
import { onMounted, provide, reactive, ref } from 'vue';

// Utils
import { useOrganisation } from '@/functions/use_remote_object';
import { useRouter } from 'vue-router';
import useAjax from '@/functions/use_ajax';

// Components
import FormRow from '@/components/form/row.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';

const createNewProcess = () => ({
  name: '',
});

const props = defineProps({
  organisationId: { type: String, required: true },
  processId: { type: String, default: null },
});

const router = useRouter();
const ajax = useAjax();
const currentOrganisation = useOrganisation(props);
const loading = ref(false);
const initialProcess = ref(null);
const process = reactive(createNewProcess());
const errors = ref(null);
const saving = ref(false);

const imageValid = ref(true);

provide('currentOrganisation', currentOrganisation);

// All endpoints that this component uses
const ENDPOINTS = {
  processes: `/organisations/${props.organisationId}/equipment_processes`,
  process: `/organisations/${props.organisationId}/equipment_processes/${props.processId}`,
  get post_or_patch() {
    return props.processId ? this.process : this.processes;
  },
};

const loadProcess = async () => {
  if (props.processId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.process);

    if (ok) {
      initialProcess.value = data;
      Object.assign(process, data);
      loading.value = false;
    }
  } else {
    initialProcess.value = createNewProcess();
  }
};

onMounted(() => {
  loadProcess();
});

const formatProcessForPost = async () => {
  let processParams = {
    name: process.name,
  };

  return { equipment_process: processParams };
};

const save = async () => {
  errors.value = null;
  saving.value = true;

  let method = props.processId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: await formatProcessForPost(),
  });

  if (ok) {
    router.replace({ name: 'equipmentProcessConfiguration' });
  } else {
    errors.value = data;
  }

  saving.value = false;
};

function validateInput(type, field) {
  if (errors.value && errors.value[type]) {
    errors.value[type][field] = null;
  }
}
</script>
